export const pick = (obj, ...keys) => {
    const ret = {};
    keys.forEach((key) => {
        ret[key] = obj[key];
    });
    return ret;
};
export const transformKeys = (object, transform) => Object.fromEntries([...Object.entries(object)].map((entry) => [transform(entry[0]), entry[1]]));
export const transformValues = (object, transform) => Object.fromEntries([...Object.entries(object)].map((entry) => [entry[0], transform(entry[1])]));
const toPaddedBase64 = (value) => {
    const normalized = value
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .replace(/\s+/g, '');
    return normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
};
export const decodeBase64ToUint8Array = (value) => Uint8Array.from(Buffer.from(toPaddedBase64(value), 'base64'));
export const decodeBase64UrlToUint8Array = (value) => decodeBase64ToUint8Array(value);
export const lowerCaseFirst = (input) => {
    return input.charAt(0).toLowerCase() + input.substring(1);
};
export const asyncFilter = async (arr, predicate) => Promise.all(arr.map(predicate)).then((results) => arr.filter((_v, index) => results[index]));
export const passThroughUndefined = (func) => (input) => input && func(input);
export const countBy = (arr, fn) => arr.map(fn).reduce((acc, val) => {
    acc[val] = (acc[val] || 0) + 1;
    return acc;
}, {});
export const groupBy = (arr, fn) => arr.reduce((acc, val) => {
    acc[fn(val)] = [...(acc[fn(val)] || []), val];
    return acc;
}, {});
export const dateSorter = () => (v0, v1) => Date.parse(v0.createdAt) - Date.parse(v1.createdAt);
export const getUniqueBy = (arr, predicate) => {
    const set = new Set();
    return arr.filter((o) => !set.has(predicate(o)) && set.add(predicate(o)));
};
export const interpolate = (text, params = {}) => {
    try {
        if (!text)
            return text;
        const names = Object.keys(params);
        const vals = Object.values(params);
        return new Function(...names, `return \`${text}\`;`)(...vals);
    }
    catch {
        throw new Error(`Failed to interpolate "${text}" with ${JSON.stringify(params)}, error`);
    }
};
export const sleep = async (seconds) => {
    await new Promise((resolve) => {
        setTimeout(resolve, seconds * 1000);
    });
};
export const deepSet = (obj, path, val) => {
    const keys = Array.isArray(path)
        ? path
        : path
            .replace(/(\w)\[/g, '$1.[')
            .split('.')
            .map((k) => k.includes('[') ? parseInt(k.substring(1, k.length - 1)) : k);
    let subObject = obj;
    for (let i = 0; i < keys.length; i++) {
        const currentKey = keys[i];
        const nextKey = keys[i + 1];
        if (typeof nextKey !== 'undefined') {
            // @ts-expect-error deepset is not really typeable
            subObject[currentKey] = subObject[currentKey]
                ? // @ts-expect-error deepset is not really typeable
                    subObject[currentKey]
                : // @ts-expect-error deepset is not really typeable
                    isNaN(nextKey)
                        ? {}
                        : [];
        }
        else {
            // @ts-expect-error deepset is not really typeable
            subObject[currentKey] = val;
        }
        // @ts-expect-error deepset is not really typeable
        subObject = subObject[currentKey];
    }
    return obj;
};
export const deepGet = (obj, path) => 
// @ts-expect-error deepget is not typeable
path.reduce((o, i) => o[i], obj);
/**
 * Checks if a value is valid => not (null, undefined, empty, etc.)
 * Special case: treats 0 as a valid value unlike standard JS truthiness
 */
export const hasValue = (v) => v === 0 || !!v;
export const kebabToCamelCase = (str) => {
    return str.replace(/-([a-z])/g, function (_match, letter) {
        return letter.toUpperCase();
    });
};
export const formatTimeStamp = (timestamp, startDate) => {
    if (!timestamp) {
        if (startDate) {
            const startDateObj = new Date(startDate * 1000);
            const nextMonth = new Date(startDateObj);
            nextMonth.setMonth(nextMonth.getMonth() + 1);
            if (nextMonth.getDate() !== startDateObj.getDate()) {
                nextMonth.setDate(0);
            }
            return nextMonth.toLocaleDateString();
        }
        return 'N/A';
    }
    return new Date(timestamp * 1000).toLocaleDateString();
};
const getMode = (config, profileSettings, systemTheme) => {
    if (profileSettings) {
        if (profileSettings.theme === "system") {
            return systemTheme;
        }
        else if ((profileSettings === null || profileSettings === void 0 ? void 0 : profileSettings.theme) === "dark") {
            return "dark";
        }
        else if ((profileSettings === null || profileSettings === void 0 ? void 0 : profileSettings.theme) === "light") {
            return "light";
        }
    }
    return config.theme.base === 'OpenpeepsDark' ? 'dark' : 'light';
};
export const getTheme = (config, profileSettings, systemTheme = getSystemTheme()) => getMode(config, profileSettings, systemTheme) === 'dark' ?
    { ...config.theme.dark, dark: true } :
    { ...config.theme.light, dark: false };
export const getSystemTheme = () => {
    if (typeof window !== "undefined" && window.matchMedia) {
        const systemIsDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        return systemIsDark ? "dark" : "light";
    }
    return 'light';
};
export const getProfileAvatar = (profile, config) => {
    return (profile === null || profile === void 0 ? void 0 : profile.avatar)
        ? profile.avatar
        : getTheme(config).defaultProfileAvatar;
};
export const getGroupAvatar = (group, config) => {
    return (group === null || group === void 0 ? void 0 : group.avatar)
        ? group.avatar
        : getTheme(config).defaultGroupAvatar;
};
export const extractDateFromUUIDv7 = (uuidV7) => {
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!UUID_REGEX.test(uuidV7)) {
        throw new Error('Invalid UUID format');
    }
    const versionChar = uuidV7[14];
    if ((parseInt(versionChar, 16) & 0xf) !== 0x7) {
        throw new Error('Not a UUIDv7 - version bits are not 7');
    }
    const hex = uuidV7.replace(/-/g, '');
    const timestampHex = hex.slice(0, 12);
    const timestampMs = parseInt(timestampHex, 16);
    const date = new Date(timestampMs);
    const pad = (num, size) => {
        let s = String(num);
        while (s.length < size) {
            s = '0' + s;
        }
        return s;
    };
    return (date.getUTCFullYear().toString() +
        pad(date.getUTCMonth() + 1, 2) + // months are zero‑based
        pad(date.getUTCDate(), 2) +
        pad(date.getUTCHours(), 2) +
        pad(date.getUTCMinutes(), 2));
};
export const formatSize = (bytes) => {
    if (bytes < 1024)
        return bytes + ' B';
    if (bytes < 1024 * 1024)
        return (bytes / 1024).toFixed(1) + ' kB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};
export const hasVisibilityChanged = (previousAudienceSetting, newAudienceSetting) => {
    var _a, _b;
    if (previousAudienceSetting.visibility !== newAudienceSetting.visibility) {
        return true;
    }
    if (previousAudienceSetting.groupId !== newAudienceSetting.groupId) {
        return true;
    }
    const prevAudience = (_a = previousAudienceSetting.audience) !== null && _a !== void 0 ? _a : [];
    const newAudience = (_b = newAudienceSetting.audience) !== null && _b !== void 0 ? _b : [];
    if (prevAudience.length !== newAudience.length) {
        return true;
    }
    const prevIds = prevAudience.map((p) => p.id).sort();
    const newIds = newAudience.map((p) => p.id).sort();
    for (let i = 0; i < prevIds.length; i++) {
        if (prevIds[i] !== newIds[i]) {
            return true;
        }
    }
    return false;
};
